import { FC, MouseEvent, useState } from 'react';
import { Avatar, Box, Chip, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { DoubleArrow, KeyboardArrowDown, Menu as MenuIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import strings from 'common/Translation/Translate';
import ConfirmModal from 'components/ConfirmModal';
import { getTableAvatarSx } from 'utils/avatarColors';
import { getInitials } from 'screens/Authenticated/SalesManagement/SalesCalls/salesCallListDisplay';
import { CompleteTaskDialog } from './MarkCompleteButton';

export const isTaskCompleted = (status?: string) => String(status || '').toUpperCase() === 'COMPLETED';

export const formatTaskDueDate = (dueDate?: string, status?: string) => {
  if (!dueDate || dueDate === 'null') {
    return { date: '—', relative: '', overdue: false, dueToday: false };
  }
  const due = dayjs(dueDate);
  if (!due.isValid()) {
    return { date: '—', relative: '', overdue: false, dueToday: false };
  }
  const date = due.format('D MMM YYYY');
  if (isTaskCompleted(status)) {
    return { date, relative: '', overdue: false, dueToday: false };
  }
  const diff = dayjs().startOf('day').diff(due.startOf('day'), 'day');
  if (diff > 0) {
    return {
      date,
      relative: diff === 1 ? '1 day overdue' : `${diff} days overdue`,
      overdue: true,
      dueToday: false,
    };
  }
  if (diff === 0) {
    return { date, relative: 'Due today', overdue: false, dueToday: true };
  }
  return { date, relative: '', overdue: false, dueToday: false };
};

const stripHtml = (value?: string) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const TaskTitleCell: FC<{
  title?: string;
  description?: string;
  onClick?: () => void;
}> = ({ title, description, onClick }) => {
  const value = title || '—';
  const plainDescription = stripHtml(description);
  const ellipsisSx = {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const;
  const tooltipTitle =
    value === '—' && !plainDescription ? (
      ''
    ) : (
      <Box>
        {value !== '—' ? (
          <Box sx={{ fontWeight: 600, mb: plainDescription ? 0.5 : 0 }}>{value}</Box>
        ) : null}
        {plainDescription ? <Box sx={{ fontWeight: 400 }}>{plainDescription}</Box> : null}
      </Box>
    );

  return (
    <Tooltip title={tooltipTitle} arrow>
      <Box
        onClick={onClick}
        sx={{
          minWidth: 0,
          py: 0.25,
          cursor: onClick ? 'pointer' : 'default',
        }}>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            lineHeight: 1.35,
            fontWeight: 600,
            color: onClick ? 'primary.main' : '#101828',
            '&:hover': onClick ? { color: 'primary.dark' } : undefined,
            ...ellipsisSx,
          }}>
          {value}
        </Typography>
        {plainDescription ? (
          <Box
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              lineHeight: 1.35,
              fontWeight: 400,
              ...ellipsisSx,
            }}>
            {plainDescription}
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );
};

export const TaskPriorityIcon: FC<{ priority?: string }> = ({ priority }) => {
  const value = String(priority || '').toUpperCase();
  const label = value ? value.charAt(0) + value.slice(1).toLowerCase() : '';
  return (
    <Tooltip title={label} arrow>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24 }}>
        {value === 'HIGH' && (
          <DoubleArrow sx={{ color: 'error.main', fontSize: 18, transform: 'rotate(-90deg)' }} />
        )}
        {value === 'MEDIUM' && <MenuIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
        {value === 'LOW' && (
          <DoubleArrow sx={{ color: 'info.main', fontSize: 18, transform: 'rotate(90deg)' }} />
        )}
      </Box>
    </Tooltip>
  );
};

export const TaskDueDateCell: FC<{ dueDate?: string; status?: string }> = ({ dueDate, status }) => {
  const { date, relative, overdue, dueToday } = formatTaskDueDate(dueDate, status);
  return (
    <Box sx={{ py: 0.25 }}>
      <Typography
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          lineHeight: 1.35,
          color: overdue ? 'error.main' : 'text.primary',
          whiteSpace: 'nowrap',
        }}>
        {date}
      </Typography>
      {relative ? (
        <Box
          sx={{
            fontSize: '0.75rem',
            lineHeight: 1.35,
            fontWeight: 500,
            color: overdue ? 'error.main' : dueToday ? '#B54708' : 'text.secondary',
            whiteSpace: 'nowrap',
          }}>
          {relative}
        </Box>
      ) : null}
    </Box>
  );
};

const pendingChipSx = {
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 24,
  bgcolor: '#FFF4E5',
  color: '#B54708',
  border: '1px solid #FFD8A8',
  '& .MuiChip-deleteIcon': { color: '#B54708', fontSize: 16, ml: -0.25 },
};

const completedChipSx = {
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 24,
  bgcolor: '#E6F4EA',
  color: '#1E7D32',
  border: '1px solid #B7E0C2',
  '& .MuiChip-deleteIcon': { color: '#1E7D32', fontSize: 16, ml: -0.25 },
};

export const TaskStatusCell: FC<{
  id: number;
  status?: string;
  onSuccess: () => void;
}> = ({ id, status, onSuccess }) => {
  const completed = isTaskCompleted(status);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [completeOpen, setCompleteOpen] = useState(false);

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const handleSelect = (next: 'PENDING' | 'COMPLETED') => {
    closeMenu();
    if (next === 'COMPLETED' && !completed) {
      setCompleteOpen(true);
    }
  };

  return (
    <>
      <Chip
        size="small"
        label={completed ? strings.taskCompleted || 'Completed' : 'Pending'}
        onClick={openMenu}
        onDelete={openMenu}
        deleteIcon={<KeyboardArrowDown />}
        sx={completed ? completedChipSx : pendingChipSx}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <MenuItem selected={!completed} onClick={() => handleSelect('PENDING')} sx={{ fontSize: 13 }}>
          Pending
        </MenuItem>
        <MenuItem selected={completed} onClick={() => handleSelect('COMPLETED')} sx={{ fontSize: 13 }}>
          {strings.taskCompleted || 'Completed'}
        </MenuItem>
      </Menu>
      <CompleteTaskDialog
        id={id}
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        onSuccess={onSuccess}
      />
    </>
  );
};

type AssigneeOption = { label: string; value: string | number };

export const TaskAssigneeCell: FC<{
  row: any;
  assignees: AssigneeOption[];
  onAssign: (taskId: number, assigneeId: number) => void;
}> = ({ row, assignees, onAssign }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingAssigneeId, setPendingAssigneeId] = useState<number | null>(null);
  const name = row?.assignedTo || assignees.find(a => String(a.value) === String(row?.assignedToUserId))?.label || '';

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const handleSelect = (assigneeId: number) => {
    closeMenu();
    if (String(assigneeId) === String(row?.assignedToUserId)) return;
    setPendingAssigneeId(assigneeId);
  };

  const confirmAssign = () => {
    if (pendingAssigneeId != null) {
      onAssign(row.id, pendingAssigneeId);
    }
    setPendingAssigneeId(null);
  };

  if (!name && !assignees.length) {
    return <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>;
  }

  return (
    <>
      <Tooltip title={name || 'Assign'} arrow>
        <IconButton size="small" onClick={openMenu} sx={{ p: 0 }} aria-label="Change assignee">
          <Avatar sx={getTableAvatarSx(name)}>{getInitials(name)}</Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        PaperProps={{ sx: { maxHeight: 320, minWidth: 200 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        {assignees.map(assignee => (
          <MenuItem
            key={assignee.value}
            selected={String(assignee.value) === String(row?.assignedToUserId)}
            onClick={() => handleSelect(Number(assignee.value))}
            sx={{ fontSize: 13, gap: 1 }}>
            <Avatar sx={getTableAvatarSx(assignee.label, 32)}>{getInitials(assignee.label)}</Avatar>
            {assignee.label}
          </MenuItem>
        ))}
      </Menu>
      {pendingAssigneeId != null && (
        <ConfirmModal
          message={strings.changeAssigneeConfirmation}
          open
          handleYes={confirmAssign}
          closeModal={() => setPendingAssigneeId(null)}
        />
      )}
    </>
  );
};
